import Page from '../../components/templates/Page'
import Container from '../../components/elements/Container'
import Section from '../../components/elements/Section'
import styles from './styles.module.scss'
import { useResponsive } from '../../providers/ResponsiveContext'
import axios from '/components/configs/axios'
import footerTranslation from '/components/general/Footer/footerTranslations'
import headerTranslation from '/components/general/Header/headerTranslation'
import cache from 'memory-cache'
import favouritesTranslation from '../../components/pages/favourites/favouritesTranslations'
import generalEstateTranslations from '/components/pages/real-estate/generalEstateTranslations'
import { useRouter } from 'next/router'
import Overview from '../../components/pages/real-estate/Overview'
import { useEffect, useState } from 'react'
import { useFavouritesContext } from '../../providers/favouritesProvider'
import isEmpty from 'lodash-es/isEmpty'
import LineSeparator from '../../components/elements/LineSeparator'
import Button from '../../components/elements/Button'
import { useAuthContext } from '../../providers/authProvider'
import Card from '../../components/elements/Card'
import Grid from '../../components/elements/Grid'
import CustomField from '../../components/elements/form/CustomField'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'

export const getStaticProps = async () => {
  const pageData = await axios
    .get('info/term')
    .then((res) => res.data)
    .catch((err) => {
      console.error(err)
    })

  let headerDataTranslation = cache.get('headerTranslations')
  if (!headerDataTranslation) {
    headerDataTranslation = await headerTranslation()
    cache.put('headerTranslations', headerDataTranslation)
  }

  // caching for footer translations
  let footerDataTranslation = cache.get('footerTranslations')
  if (!footerDataTranslation) {
    footerDataTranslation = await footerTranslation()
    cache.put('footerTranslations', footerDataTranslation)
  }

  let generalTranslation = cache.get('generalRealEstateTranslations')
  if (!generalTranslation) {
    generalTranslation = await generalEstateTranslations()
    cache.put('generalRealEstateTranslations', generalTranslation)
  }

  let pageTranslations = await favouritesTranslation()

  return {
    props: {
      pageData: pageData,
      headerTranslation: headerDataTranslation,
      footerTranslation: footerDataTranslation,
      generalTranslation,
      pageTranslations
    },
    revalidate: 300
  }
}

const Favourites = ({
  generalTranslation,
  headerTranslation,
  footerTranslation,
  pageTranslations
}) => {
  const { favourites } = useFavouritesContext()
  const { loggedIn } = useAuthContext()
  const { isTablet, isSmallMobile } = useResponsive()
  const router = useRouter()
  const [success, setSuccess] = useState('')

  const [properties, setProperties] = useState({})

  const hasProperties =
    !isEmpty(properties?.realEstate?.[router.locale]) ||
    !isEmpty(properties?.vacationRentals?.[router.locale])

  const formInitialValues = {
    name: '',
    email: ''
  }

  const FormValidation = Yup.object().shape({
    name: Yup.string()
      .max(25)
      .matches(
        /^'?\p{L}+(?:[' ]\p{L}+)*'?$/u,
        pageTranslations.form.error.validName[router.locale]
      )
      .required(pageTranslations.form.error.requiredFirstName[router.locale]),

    email: Yup.string()
      .max(254)
      .matches(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        pageTranslations.form.error.validEmail[router.locale]
      )
      .email(pageTranslations.form.error.validEmail[router.locale])
      .required(pageTranslations.form.error.requiredEmail[router.locale])
  })

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    setSubmitting(true)

    let realEstateLinks =
      properties?.realEstate?.en?.map(
        (realEstate) =>
          // eslint-disable-next-line no-undef
          `${process.env.NEXT_PUBLIC_DOMAIN_URL}/real-estate/${realEstate.slug}`
      ) || []
    const vacationRentalsLinks =
      properties?.vacationRentals?.en?.map(
        (vacationRental) =>
          // eslint-disable-next-line no-undef
          `${process.env.NEXT_PUBLIC_DOMAIN_URL}/vacation-rentals/${
            vacationRental.nickname
          }-${vacationRental.bedrooms}-bedroom-${(
            vacationRental.propertyType?.en || 'villa'
          ).toLowerCase()}`
      ) || []

    axios
      .post('favourites/send-email', {
        ...values,
        content: [...realEstateLinks, ...vacationRentalsLinks]
      })
      .then(() => {
        setSuccess(true)
        resetForm()
      })
      .catch((err) => {
        setSuccess(false)
        console.error(err)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  useEffect(() => {
    if (!isEmpty(favourites)) {
      axios
        .get('favourites/details', {
          params: {
            ids: [...favourites]
          }
        })
        .then((res) => {
          const data = res.data

          // Handle both array format and legacy object format
          if (Array.isArray(data)) {
            // API returns flat array - transform to expected structure
            const realEstate = data.filter(
              (p) => p && typeof p === 'object' && p.type === 'real-estate'
            )
            const vacationRentals = data.filter(
              (p) => p && typeof p === 'object' && p.type !== 'real-estate'
            )

            setProperties({
              realEstate:
                realEstate.length > 0
                  ? {
                      en: realEstate,
                      fr: realEstate,
                      es: realEstate,
                      nl: realEstate
                    }
                  : null,
              vacationRentals:
                vacationRentals.length > 0
                  ? {
                      en: vacationRentals,
                      fr: vacationRentals,
                      es: vacationRentals,
                      nl: vacationRentals
                    }
                  : null
            })
          } else {
            // Legacy format - use as-is
            setProperties(data)
          }
        })
        .catch((err) => {
          console.error('[FavouritesPage] API error:', err)
        })
    } else {
      setProperties({})
    }
  }, [favourites])

  return (
    <Page
      headerTranslation={headerTranslation}
      footerTranslation={footerTranslation}
      footer={false}
      meta={{
        title: pageTranslations.seo.title.en,
        description: pageTranslations.seo.description.en
      }}
    >
      <Section>
        <div className={styles.intro}>
          <h1 className={styles.intro__title}>
            {pageTranslations.page.title[router.locale]}
          </h1>

          <LineSeparator />

          <p className={styles.intro__subtitle}>
            {!hasProperties
              ? pageTranslations.page.emptyProperties[router.locale]
              : pageTranslations.page.description[router.locale]}
          </p>

          {!hasProperties && (
            <Button variant={['m-t-3xl']} href="/vacation-rentals">
              {pageTranslations.page.browseProperties[router.locale]}
            </Button>
          )}
        </div>
      </Section>

      <Overview
        totalCards={isSmallMobile ? 1 : isTablet ? 2 : 3}
        unlimited={true}
        properties={properties?.realEstate?.[router.locale]}
        generalTranslation={generalTranslation}
        buttonLabel={false}
      />

      {!isEmpty(properties?.vacationRentals?.[router.locale]) && (
        <Section variant={['center', 'primary']}>
          <Container>
            <h2 className={styles.intro__title}>
              {pageTranslations.page.vacationRentals[router.locale]}
            </h2>

            <Grid variant={[`grid-${isSmallMobile ? 1 : 2}`]}>
              {properties?.vacationRentals?.[router.locale].map(
                (property, index) => (
                  <Card
                    translations={headerTranslation.card}
                    key={property.resource_id || property.id || index}
                    {...property}
                  />
                )
              )}
            </Grid>
          </Container>
        </Section>
      )}

      {hasProperties && (
        <Section variant={['center', 'white']}>
          <Container variant={['-xs4']}>
            <h2
              className={styles.subtitle}
              dangerouslySetInnerHTML={{
                __html: pageTranslations.form.title[router.locale]
              }}
            ></h2>

            <p className={styles.description}>
              {pageTranslations.form.description[router.locale]}
            </p>

            <Formik
              validationSchema={FormValidation}
              initialValues={formInitialValues}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, values }) => {
                return (
                  <Form>
                    <Grid variant={['grid-2', 'spacing-xs']}>
                      <CustomField
                        required
                        name="name"
                        isActive={values.name}
                        maxLength={254}
                        placeholder={
                          pageTranslations.form.fields.name[router.locale]
                        }
                        variant={['gray', 'underline', 'full-width']}
                        error={errors.name && touched.name}
                        valid={values.name && !errors.name && touched.name}
                      />

                      <CustomField
                        required
                        name="email"
                        type="email"
                        isActive={values.email}
                        maxLength={254}
                        placeholder={
                          pageTranslations.form.fields.email[router.locale]
                        }
                        variant={['gray', 'underline', 'full-width']}
                        error={errors.email && touched.email}
                        valid={values.email && !errors.email && touched.email}
                      />
                    </Grid>

                    <Button
                      disabled={
                        errors.email ||
                        errors.name ||
                        values.name === '' ||
                        values.email === ''
                      }
                      variant={['primary-blue', 'full-width', 'm-t-xl']}
                    >
                      {pageTranslations.form.fields.submit[router.locale]}
                    </Button>

                    <div className={styles.confirmation}>
                      {success === '' ? (
                        <></>
                      ) : success ? (
                        <p className={styles.success}>
                          {pageTranslations.form.success[router.locale]}
                        </p>
                      ) : (
                        <p className={styles.error}>
                          {pageTranslations.form.fail[router.locale]}
                        </p>
                      )}
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </Container>
        </Section>
      )}

      {!loggedIn && !isEmpty(favourites) && (
        <Section variant={['gray', 'center']}>
          <Container>
            <h2
              className={styles.subtitle}
              dangerouslySetInnerHTML={{
                __html: pageTranslations.page.save[router.locale]
              }}
            ></h2>

            <Button href="/login" variant={['primary-blue']}>
              {pageTranslations.page.login[router.locale]}
            </Button>
          </Container>
        </Section>
      )}
    </Page>
  )
}

export default Favourites
